import { z } from 'zod';
import { documentSchema } from './IDocument';

// Toleranter als documentSchema: Join-Zeilen aus Task_has_Document liefern nur
// Metadaten (idDocument, FileName, idMIMEType, Created_at), NICHT
// idDocumentDirectory / idLegalTransaction. Diese werden hier auf null
// defaulted, damit der strikte taskFullObjectSchema.parse() nicht scheitert.
export const taskDocumentSchema = documentSchema.extend({
  idDocumentDirectory: z.number().nullable().default(null),
  idLegalTransaction: z.number().nullable().default(null),
});

export const taskSchema = z.object({
  idTask: z.number().default(-1),
  idTaskType: z.number().default(1),
  idTaskPriority: z.number().default(1),
  idUser: z.number().default(1),

  Created_at: z.string().default(new Date().toISOString().split('T')[0]),
  Updated_at: z.string().default(new Date().toISOString().split('T')[0]),

  Deadline_at: z.string().default(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toISOString().split('T')[0];
  }),

  Reminder_at: z.string().nullable().default(null),

  wasRemembered: z
    .union([z.boolean(), z.number().transform(val => Boolean(val))])
    .transform(val => Boolean(val))
    .default(false),

  Title: z.string().default(''),
  Body: z.string().default(''),

  idLegalTransaction: z.number().nullable().default(null),
  idLegalClient: z.number().nullable().default(null),
  Closed_at: z
    .preprocess(val => {
      if (val === null || val === undefined) return null;
      if (val instanceof Date) return val;
      if (typeof val === 'string') return new Date(val);
      return null;
    }, z.date().nullable())
    .default(null),
  // #1244: von einer Maßnahme erzeugte/zugeordnete Aufgabe.
  LegalRequirement_idLegalRequirement: z.number().nullable().default(null),
  // #1244: transientes Flag — beim Erstellen wird der/die Vorgangs-Zuständige
  // aufgelöst (nicht persistiert).
  AssignToTransactionResponsible: z.boolean().optional(),
  // Aus den Relationen
  TaskPriority: z.string().optional(),
  // #1210: Ersteller (Aufgabengeber) aus der User-Relation — nur lesend,
  // wird beim Speichern vom Backend ignoriert.
  FirstName: z.string().nullable().optional(),
  LastName: z.string().nullable().optional(),
});

// #1190: Kommentar an einer Aufgabe (Ersteller-Namen aus der User-Relation).
export const taskCommentSchema = z.object({
  idTaskComment: z.number().default(-1),
  idTask: z.number().optional(),
  idUser: z.number().default(-1),
  Comment: z.string().default(''),
  Created_at: z.string().default(() => new Date().toISOString()),
  isEdited: z
    .union([z.boolean(), z.number().transform(val => Boolean(val))])
    .transform(val => Boolean(val))
    .default(false),
  FirstName: z.string().nullable().optional(),
  LastName: z.string().nullable().optional(),
  // Per @ erwähnte Benutzer — Basis für die Benachrichtigung (Backend).
  MentionedUserIds: z.number().array().default([]),
});

// #1248: Log-Eintrag (read-only, Akteur-Namen aus der User-Relation).
export const taskLogSchema = z.object({
  idTaskLog: z.number(),
  idTask: z.number().optional(),
  idUser: z.number().nullable().optional(),
  LogText: z.string().nullable().default(''),
  Created_at: z.string().optional(),
  FirstName: z.string().nullable().optional(),
  LastName: z.string().nullable().optional(),
});

export const taskFullObjectSchema = taskSchema.extend({
  Users: z.number().array().default([]),
  UserGroups: z.number().array().default([]),
  // optional (ohne Default): nur gesetzt, wenn Dokumente geladen/bearbeitet
  // wurden. Fehlt der Schlüssel, lässt das Backend die Dokumente unberührt
  // (verhindert versehentliches Löschen beim Vorgang-Speichern).
  DocumentArray: z.array(taskDocumentSchema).optional(),
  // #1190/#1248: gleiche Semantik wie DocumentArray (fehlender Schlüssel =
  // unangetastet); LogArray ist rein lesend.
  CommentArray: z.array(taskCommentSchema).optional(),
  LogArray: z.array(taskLogSchema).optional(),
});

export type ITask = z.infer<typeof taskSchema>;
export type ITaskFullObject = z.infer<typeof taskFullObjectSchema>;
export type ITaskComment = z.infer<typeof taskCommentSchema>;
export type ITaskLog = z.infer<typeof taskLogSchema>;
